<!--
// app/components/region-stats/cards-featured-stats/CardBestGoalkeeper.vue
-->
<script setup lang="ts">
import type { RankingsData } from "~/src/player/domain/Player";
import { useImages } from "~/app/composables/images";
import { useRegions } from "~/app/composables/regions";
import { useWebContext } from "~/app/composables/webContext";
import { setAliasUrl } from "~/src/Shared/utils";
import { initDropdowns } from "flowbite";
import InfoIcon from "~/app/components/Shared/icons/InfoIcon.vue";
import PlaceholderTeamShield from "~/app/components/pages/region-stats/cards-featured-stats/PlaceholderTeamShield.vue";
import { useSeasons } from "~/app/composables/season/seasons";

type Props = {
  seasonId: number | string;
  pageStatsLink: string;
  disclaimer?: string;
  isStats?: boolean; // Usage is in /stats
  isBestGk?: boolean;
  bestGkId?: string | number;
};

const props = defineProps<Props>();

const localePath = useLocalePath();
const route = useRoute();
const { regionParam, isRegionHome, isSpain, isWorldCup, isNations, isGermany } = useRegions();
const { playerPlaceholderPath, videoBgFeaturedStatsPath, posterBgFeaturedStatsPath } = useImages();
const { isKings } = useWebContext();
const { useFetchParamsSeasonStatsFirstPlayer, getSeasonParameterStats } = useSeasons();

const queries = computed(() => {
  return route.query ? route.query : undefined;
});

const videoBg = ref<HTMLVideoElement | null>(null);

let isPlaying = false;
let playPromise = null;

const playVideoBg = () => {
  if (videoBg.value && !isPlaying) {
    playPromise = videoBg.value.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          isPlaying = true;
        })
        .catch(error => {
          console.warn("play() failed:", error);
        });
    }
  }
};

const pauseVideoBg = () => {
  if (videoBg.value && isPlaying) {
    if (playPromise !== null) {
      playPromise.finally(() => {
        videoBg.value.pause();
        isPlaying = false;
      });
    } else {
      videoBg.value.pause();
      isPlaying = false;
    }
  }
};

const bestGoalkeeper = ref<RankingsData[]>([]);
const showSaves = ref<boolean>(false);

if (props.isBestGk && !!props.bestGkId) {
  showSaves.value = false;

  const stats = await getSeasonParameterStats(
    Number(props.seasonId),
    "GOL-S",
    true,
    "rankingAVG",
    "DESC",
    isWorldCup ? "50" : undefined
  );

  const bestGkRankingItem = !!stats ? stats.find(r => r.player.id === Number(props.bestGkId)) : undefined;
  bestGoalkeeper.value = !!bestGkRankingItem ? [bestGkRankingItem] : [];
} else {
  showSaves.value = true;

  const fetchOptsGoalkeeperSaves = props.seasonId
    ? useFetchParamsSeasonStatsFirstPlayer(Number(props.seasonId), "PRT")
    : undefined;
  const { path: pathSv, opts: optsSv } = fetchOptsGoalkeeperSaves;
  const { data } = await useFetch(pathSv, optsSv);

  bestGoalkeeper.value = [...data.value];
}

const disclaimerId = useId() || "disclaimer-dd";
const disclaimerBtnId = useId() || "disclaimer-btn-dd";

onMounted(() => {
  initDropdowns();
});
</script>

<template>
  <div
    class="flex flex-col max-w-[343px] w-full min-w-full lg:max-w-none 2xl:w-auto 2xl:aspect-[325/568]"
    @mouseenter="playVideoBg"
    @mouseleave="pauseVideoBg"
  >
    <div class="card-player-image-container">
      <nuxt-link
        :to="
          localePath({
            name: `region-stats-${pageStatsLink}`,
            params: { region: regionParam },
            query: queries,
          })
        "
        :no-prefetch="!!isRegionHome"
      >
        <video
          :src="videoBgFeaturedStatsPath"
          :poster="posterBgFeaturedStatsPath"
          class="absolute top-0 left-0 right-0 bottom-0 min-h-[405px] sm:min-h-[450px]] object-cover object-top"
          ref="videoBg"
          width="100%"
          height="100%"
          loop
          muted
        ></video>
        <div class="image-container">
          <NuxtImg
            :src="
              bestGoalkeeper?.length && bestGoalkeeper[0].player?.image?.url
                ? setAliasUrl(bestGoalkeeper[0].player?.image?.url)
                : playerPlaceholderPath
            "
            :alt="bestGoalkeeper && bestGoalkeeper?.length ? bestGoalkeeper[0].player?.shortName : ''"
            class="object-top object-cover min-h-[463px] sm:min-h-[546px]"
            width="456"
            height="642"
            sizes="256px sm:472px md:325px"
          />
        </div>
        <div class="gradient-container">
          <div>
            <div v-if="!showSaves" class="pre-title-stat">{{ $t("cards.stats.pretitle.best") }}</div>
            <div class="title-stat">
              {{ showSaves ? $t("cards.stats.title.saves") : $t("cards.stats.title.goalkeeper") }}
            </div>
          </div>
        </div>
      </nuxt-link>

      <div class="mt-4 table-first-stats absolute min-w-max left-0 bottom-0 right-0 p-1">
        <div class="flex items-center justify-end text-gray-400 text-[8.5px] sm:text-[10px] uppercase px-1">
          <span class="w-1/6 text-center me-1 min-w-min">{{ $t("cards.stats.team.text") }}</span>
          <span class="w-4/6 text-left rtl:text-right">{{ $t("cards.stats.name.text") }}</span>
          <span class="w-1/6 text-center">{{ showSaves ? $t("Par.") : "Ratio" }}</span>
        </div>
        <div
          v-for="(ranking, i) in bestGoalkeeper?.length ? bestGoalkeeper : 3"
          :key="ranking?.player?.id || i"
          class="flex items-center justify-end text-sm font-bold px-1 bg-gray-800 bg-opacity-50 rounded-md mt-1"
        >
          <div class="w-1/6 me-1 min-w-min logo-team-container">
            <NuxtImg
              v-if="ranking?.team?.logo?.url"
              :src="setAliasUrl(ranking?.team?.logo?.url)"
              alt="Team"
              class="object-contain h-8 w-8 lg:w-10 lg:h-10"
              loading="lazy"
              height="40"
              width="40"
            />
            <span v-else class="text-gray-700"
              ><placeholder-team-shield :width="40" :height="40"></placeholder-team-shield
            ></span>
          </div>
          <component
            :is="isStats ? 'h2' : 'div'"
            class="w-4/6 text-left rtl:text-right me-auto text-xs sm:text-sm !leading-[1.1]"
          >
            {{ ranking?.player?.shortName || $t("general.player.word") }}
          </component>
          <div class="w-1/6 flex items-center justify-center">
            {{
              !ranking || !ranking.player
                ? 0
                : showSaves
                  ? Number(ranking?.total)
                  : Number(ranking?.totalAVG).toFixed(2)
            }}
          </div>
        </div>
      </div>

      <button
        :id="disclaimerBtnId"
        v-if="disclaimer"
        class="disclaimer"
        :data-dropdown-toggle="disclaimerId"
        data-dropdown-placement="right"
      >
        <info-icon height="25" width="25"></info-icon>
      </button>
      <div
        :id="disclaimerId"
        class="z-10 hidden bg-gray-700 rounded-lg shadow w-[200px] text-sm p-2"
        :aria-labelledby="disclaimerBtnId"
      >
        {{ disclaimer }}
      </div>
    </div>

    <div class="text-center py-2" v-if="regionParam && pageStatsLink">
      <nuxt-link
        :to="
          localePath({
            name: `region-stats-${pageStatsLink}`,
            params: { region: regionParam },
            query: queries,
          })
        "
        class="see-more-link"
        :no-prefetch="!!isRegionHome"
        >{{ $t("cards.stats.seeMore.link") }}
      </nuxt-link>
    </div>
  </div>
</template>

<style scoped>
.card-player-image-container {
  @apply min-h-[463px] sm:min-h-[546px] px-1 py-2 rounded-lg relative overflow-hidden bg-no-repeat bg-cover bg-center;
}

.image-container {
  @apply h-full absolute top-0 left-0 right-0 bottom-0 flex flex-col justify-start items-center pt-5;
}

.gradient-container {
  @apply absolute min-w-full min-h-full top-0 right-0 left-0 bottom-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.0)40%,rgba(0,0,0,1)100%)] px-1 py-2 flex flex-col justify-between;
}

.pre-title-stat {
  @apply text-primary-light text-center text-sm uppercase font-['Archivo_Expanded'] font-bold leading-[14px];
}

.title-stat {
  @apply text-black text-center text-lg sm:text-2xl font-bold uppercase font-['Archivo_Expanded'] leading-none;
  -webkit-text-stroke: 1px theme("colors.primary.light");
  text-stroke: 1px theme("colors.primary.light");
}

.logo-team-container {
  @apply flex flex-col justify-center items-center bigsm:p-0.5;
}

.see-more-link {
  @apply text-xs text-gray-400;
}

.disclaimer {
  @apply text-primary absolute right-2 top-2;
}
</style>
